import EventEmitter2 from 'eventemitter2';
import { diffUpdate, encodeStateVectorFromUpdate, mergeUpdates } from 'yjs';

import type { Connection } from '../connection';
import { isEmptyUpdate } from '../utils/is-empty-update';
import { isQuotaExceededError } from '../utils/quota';
import type { Locker } from './lock';
import { SingletonLocker } from './lock';
import { type Storage } from './storage';

export interface BlockInfo {
  blockId: string;
  flavour: string;
  sourceType?: string;
  content?: string[];
  blob?: string[];
  refDocId?: string[];
  refInfo?: string[];
  parentFlavour?: string;
  parentBlockId?: string;
  additional?: string;
}

export interface CrawlResult {
  blocks: BlockInfo[];
  title: string;
  summary: string;
}

export interface DocClock {
  docId: string;
  timestamp: Date;
}

export type DocClocks = Record<string, Date>;
export interface DocRecord extends DocClock {
  bin: Uint8Array;
  editor?: string;
}

export interface DocDiff extends DocClock {
  missing: Uint8Array;
  state: Uint8Array;
}

export interface DocUpdate {
  docId: string;
  bin: Uint8Array;
  editor?: string;
  requestId?: string;
}

export interface Editor {
  name: string;
  avatarUrl: string | null;
}

export interface CorruptionReport {
  docId: string;
  timestamp: string;
  error: string;
  binLength: number;
  binPreview: number[];
  docTimestamp?: Date;
}

export interface DocStorageOptions {
  mergeUpdates?: (updates: Uint8Array[]) => Promise<Uint8Array> | Uint8Array;
  id: string;

  /**
   * open as readonly mode.
   */
  readonlyMode?: boolean;
}

export interface DocStorage extends Storage {
  readonly storageType: 'doc';
  readonly isReadonly: boolean;
  readonly spaceId: string;
  /**
   * Get a doc record with latest binary.
   */
  getDoc(docId: string): Promise<DocRecord | null>;
  /**
   * Get a yjs binary diff with the given state vector.
   */
  getDocDiff(docId: string, state?: Uint8Array): Promise<DocDiff | null>;
  /**
   * Push updates into storage
   *
   * @param origin - Internal identifier to recognize the source in the "update" event. Will not be stored or transferred.
   */
  pushDocUpdate(update: DocUpdate, origin?: string): Promise<DocClock>;

  /**
   * Get the timestamp of the latest update of a doc.
   */
  getDocTimestamp(docId: string): Promise<DocClock | null>;

  /**
   * Get all docs timestamps info. especially for useful in sync process.
   */
  getDocTimestamps(after?: Date, limit?: number): Promise<DocClocks>;

  /**
   * Delete a specific doc data with all snapshots and updates
   */
  deleteDoc(docId: string): Promise<void>;

  /**
   * Delete only the local storage copy for a doc.
   *
   * This must not cascade to remote storage. Use it for local cache/draft
   * cleanup paths where the server either already owns the canonical state or
   * may not know the doc yet.
   */
  resetLocalDoc?(docId: string): Promise<void>;

  /**
   * Replace only the local copy of one doc with a confirmed canonical record.
   *
   * Implementations must swap snapshot, pending updates, and clock atomically.
   * This is intentionally optional: callers must not attempt local poison
   * rebuilds when the active local storage cannot provide atomic replacement.
   */
  replaceLocalDoc?(record: DocRecord, origin?: string): Promise<DocClock>;

  /**
   * Subscribe on doc updates emitted from storage itself.
   *
   * NOTE:
   *
   *   There is not always update emitted from storage itself.
   *
   *   For example, in Sqlite storage, the update will only come from user's updating on docs,
   *   in other words, the update will never somehow auto generated in storage internally.
   *
   *   But for Cloud storage, there will be updates broadcasted from other clients,
   *   so the storage will emit updates to notify the client to integrate them.
   */
  subscribeDocUpdate(
    callback: (update: DocRecord, origin?: string) => void
  ): () => void;

  /**
   * sl-affine-uhr: Subscribe to compaction events.
   * Called when a doc has been compacted on the server.
   * Clients should clear local cache and pull fresh state.
   */
  subscribeDocCompacted(
    callback: (info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }) => void
  ): () => void;

  /**
   * sl-j143.5: Subscribe to permission change events.
   * Called when the user's doc/folder permissions change on the server.
   */
  subscribePermissionsChanged(
    callback: (info: {
      eventId?: string;
      workspaceId: string;
      changeType: string;
      timestamp: number;
      permissionGraphVersion?: number;
      refreshMode?: 'scoped' | 'lazy' | 'full';
      affectedFolderIds?: string[];
      affectedDocIds?: string[];
      docPlacementChanges?: Array<{
        docId: string;
        fromFolderId: string | null;
        toFolderId: string;
      }>;
      folderPlacementChanges?: Array<{
        folderId: string;
        fromParentId: string | null;
        toParentId: string;
      }>;
      revokedFolderIds?: string[];
    }) => void
  ): () => void;

  /**
   * Subscribe to workspace meta change events.
   * Called when workspace metadata changes (e.g. folder_deleted).
   * Propagates workspace:meta-changed WebSocket events through the sync chain.
   */
  subscribeWorkspaceMetaChanged(
    callback: (info: {
      workspaceId: string;
      changeType: string;
      docId?: string | null;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }) => void
  ): () => void;

  /**
   * sl-lkdb.3.1: Subscribe to recovery completed events.
   * Called when PITR recovery completes — clients should clear IDB and re-sync.
   */
  subscribeRecoveryCompleted(
    callback: (info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }) => void
  ): () => void;

  /**
   * sl-lv2w.5: Subscribe to doc size warning events (soft limit).
   * Called when a doc crosses the size warning threshold.
   */
  subscribeDocSizeWarning(
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }) => void
  ): () => void;

  /**
   * sl-lv2w.5: Subscribe to doc size hard limit events.
   * Called when a doc crosses the hard size limit — new inserts should be blocked.
   */
  subscribeDocSizeHardLimit(
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }) => void
  ): () => void;

  /**
   * sl-00ro: Subscribe to embedding doc status events.
   */
  subscribeEmbeddingDocStatus(
    callback: (info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }) => void
  ): () => void;

  /**
   * sl-00ro: Subscribe to embedding workspace progress events.
   */
  subscribeEmbeddingWorkspaceProgress(
    callback: (info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }) => void
  ): () => void;

  /**
   * Subscribe to knowledge graph update events.
   */
  subscribeKgUpdated(
    callback: (info: {
      workspaceId: string;
      entityCount: number;
      relationCount: number;
      timestamp: number;
    }) => void
  ): () => void;

  crawlDocData?(docId: string): Promise<CrawlResult | null>;

  /**
   * Surgically repair a corrupt doc by removing only corrupt IDB entries.
   * Validates snapshot and each individual update, deletes only corrupt ones.
   * Preserves all valid unsynced data — safe for workspace root and all docs.
   */
  repairDoc?(docId: string): Promise<DocRepairResult>;
}

export interface DocRepairResult {
  /** Number of valid update entries kept */
  kept: number;
  /** Number of corrupt update entries removed */
  removed: number;
  /** Whether the snapshot was corrupt and had to be rebuilt/removed */
  snapshotRemoved: boolean;
  /** Whether the doc is now readable after repair */
  docReadable: boolean;
}

export abstract class DocStorageBase<Opts = {}> implements DocStorage {
  get isReadonly(): boolean {
    return this.options.readonlyMode ?? false;
  }
  private readonly event = new EventEmitter2();
  readonly storageType = 'doc';
  abstract readonly connection: Connection;
  protected readonly locker: Locker = new SingletonLocker();
  readonly spaceId = this.options.id;

  constructor(protected readonly options: Opts & DocStorageOptions) {}

  async getDoc(docId: string) {
    await using _lock = this.isReadonly
      ? undefined
      : await this.lockDocForUpdate(docId);

    const snapshot = await this.getDocSnapshot(docId);
    const updates = await this.getDocUpdates(docId);

    if (updates.length) {
      const { timestamp, bin, editor } = await this.squash(
        snapshot ? [snapshot, ...updates] : updates
      );

      const newSnapshot = {
        spaceId: this.spaceId,
        docId,
        bin,
        timestamp,
        editor,
      };

      // if is readonly, we will not set the new snapshot
      if (!this.isReadonly) {
        try {
          await this.setDocSnapshot(newSnapshot, snapshot);

          // always mark updates as merged unless throws
          await this.markUpdatesMerged(docId, updates);
        } catch (error) {
          if (!isQuotaExceededError(error)) {
            throw error;
          }

          console.warn(
            `[DocStorage] Skipped snapshot compaction for ${docId} because local IndexedDB quota is exceeded. Unmerged updates are preserved.`
          );
        }
      }

      return newSnapshot;
    }

    return snapshot;
  }

  async getDocDiff(docId: string, state?: Uint8Array) {
    const doc = await this.getDoc(docId);

    if (!doc) {
      return null;
    }

    try {
      return {
        docId,
        missing:
          state && state.length > 0 ? diffUpdate(doc.bin, state) : doc.bin,
        state: encodeStateVectorFromUpdate(doc.bin),
        timestamp: doc.timestamp,
      };
    } catch (error) {
      // Local doc data is corrupt - preserve for analysis, don't delete!
      // Deletion would cause data loss in offline/local-first scenarios
      const errorMsg = error instanceof Error ? error.message : String(error);
      const corruptionReport = {
        docId,
        timestamp: new Date().toISOString(),
        error: errorMsg,
        binLength: doc.bin.length,
        binPreview: Array.from(doc.bin.slice(0, 50)),
        docTimestamp: doc.timestamp,
      };
      console.error(
        `[DocStorage] CORRUPT DATA DETECTED - preserving for analysis:`,
        JSON.stringify(corruptionReport)
      );
      // Store corruption report for later analysis
      this.storeCorruptionReport(corruptionReport).catch(() => {});
      // Re-throw to let caller handle (don't silently delete data!)
      throw new Error(`Corrupt local data for doc ${docId}: ${errorMsg}`);
    }
  }

  abstract pushDocUpdate(update: DocUpdate, origin?: string): Promise<DocClock>;

  abstract getDocTimestamp(docId: string): Promise<DocClock | null>;

  abstract getDocTimestamps(after?: Date, limit?: number): Promise<DocClocks>;

  abstract deleteDoc(docId: string): Promise<void>;

  /**
   * Store corruption report for analysis. Implementations should persist this
   * and implement log rotation (max 100 reports, FIFO).
   */
  protected async storeCorruptionReport(
    report: CorruptionReport
  ): Promise<void> {
    // Default implementation uses localStorage with rotation
    const STORAGE_KEY = `affine:corruption-reports:${this.spaceId}`;
    const MAX_REPORTS = 100;

    try {
      const existing = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || '[]'
      ) as CorruptionReport[];
      existing.push(report);
      // FIFO rotation - keep only last MAX_REPORTS
      while (existing.length > MAX_REPORTS) {
        existing.shift();
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    } catch {
      // localStorage might not be available (worker context)
      // Subclasses can override with IndexedDB implementation
    }
  }

  /**
   * Get stored corruption reports for analysis.
   */
  getCorruptionReports(): CorruptionReport[] {
    const STORAGE_KEY = `affine:corruption-reports:${this.spaceId}`;
    try {
      return JSON.parse(
        localStorage.getItem(STORAGE_KEY) || '[]'
      ) as CorruptionReport[];
    } catch {
      return [];
    }
  }

  subscribeDocUpdate(callback: (update: DocRecord, origin?: string) => void) {
    this.event.on('update', callback);

    return () => {
      this.event.off('update', callback);
    };
  }

  /**
   * sl-affine-uhr: Subscribe to compaction events.
   * Called when a doc has been compacted on the server.
   * Clients should clear local cache and pull fresh state.
   */
  subscribeDocCompacted(
    callback: (info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }) => void
  ) {
    this.event.on('compacted', callback);
    return () => {
      this.event.off('compacted', callback);
    };
  }

  /**
   * sl-j143.5: Subscribe to permission change events.
   * Called when the user's doc/folder permissions change on the server.
   * Clients should refresh their accessible doc cache.
   */
  subscribePermissionsChanged(
    callback: (info: {
      eventId?: string;
      workspaceId: string;
      changeType: string;
      timestamp: number;
      permissionGraphVersion?: number;
      refreshMode?: 'scoped' | 'lazy' | 'full';
      affectedFolderIds?: string[];
      affectedDocIds?: string[];
      docPlacementChanges?: Array<{
        docId: string;
        fromFolderId: string | null;
        toFolderId: string;
      }>;
      folderPlacementChanges?: Array<{
        folderId: string;
        fromParentId: string | null;
        toParentId: string;
      }>;
      revokedFolderIds?: string[];
    }) => void
  ) {
    this.event.on('permissionsChanged', callback);
    return () => {
      this.event.off('permissionsChanged', callback);
    };
  }

  /**
   * Subscribe to workspace meta change events.
   * Called when workspace metadata changes (e.g. folder_deleted).
   */
  subscribeWorkspaceMetaChanged(
    callback: (info: {
      workspaceId: string;
      changeType: string;
      docId: string;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }) => void
  ) {
    this.event.on('workspaceMetaChanged', callback);
    return () => {
      this.event.off('workspaceMetaChanged', callback);
    };
  }

  /**
   * sl-lkdb.3.1: Subscribe to recovery completed events.
   * Called when PITR recovery completes — clients should clear IDB and re-sync.
   */
  subscribeRecoveryCompleted(
    callback: (info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }) => void
  ) {
    this.event.on('recoveryCompleted', callback);
    return () => {
      this.event.off('recoveryCompleted', callback);
    };
  }

  /**
   * sl-lv2w.5: Subscribe to doc size warning events (soft limit).
   */
  subscribeDocSizeWarning(
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }) => void
  ) {
    this.event.on('docSizeWarning', callback);
    return () => {
      this.event.off('docSizeWarning', callback);
    };
  }

  /**
   * sl-lv2w.5: Subscribe to doc size hard limit events.
   */
  subscribeDocSizeHardLimit(
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }) => void
  ) {
    this.event.on('docSizeHardLimit', callback);
    return () => {
      this.event.off('docSizeHardLimit', callback);
    };
  }

  /**
   * sl-00ro: Subscribe to embedding doc status events.
   */
  subscribeEmbeddingDocStatus(
    callback: (info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }) => void
  ) {
    this.event.on('embeddingDocStatus', callback);
    return () => {
      this.event.off('embeddingDocStatus', callback);
    };
  }

  /**
   * sl-00ro: Subscribe to embedding workspace progress events.
   */
  subscribeEmbeddingWorkspaceProgress(
    callback: (info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }) => void
  ) {
    this.event.on('embeddingWorkspaceProgress', callback);
    return () => {
      this.event.off('embeddingWorkspaceProgress', callback);
    };
  }

  /**
   * Subscribe to knowledge graph update events.
   */
  subscribeKgUpdated(
    callback: (info: {
      workspaceId: string;
      entityCount: number;
      relationCount: number;
      timestamp: number;
    }) => void
  ) {
    this.event.on('kgUpdated', callback);
    return () => {
      this.event.off('kgUpdated', callback);
    };
  }

  async crawlDocData(_docId: string): Promise<CrawlResult | null> {
    return null;
  }

  // REGION: api for internal usage
  protected on(
    event: 'update',
    callback: (update: DocRecord, origin: string) => void
  ): () => void;
  protected on(
    event: 'snapshot',
    callback: (snapshot: DocRecord, prevSnapshot: DocRecord | null) => void
  ): () => void;
  // sl-affine-uhr: Listen for compaction events
  protected on(
    event: 'compacted',
    callback: (info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }) => void
  ): () => void;
  // sl-j143.5: Listen for permission change events
  protected on(
    event: 'permissionsChanged',
    callback: (info: {
      eventId?: string;
      workspaceId: string;
      changeType: string;
      timestamp: number;
      permissionGraphVersion?: number;
      refreshMode?: 'scoped' | 'lazy' | 'full';
      affectedFolderIds?: string[];
      affectedDocIds?: string[];
      docPlacementChanges?: Array<{
        docId: string;
        fromFolderId: string | null;
        toFolderId: string;
      }>;
      folderPlacementChanges?: Array<{
        folderId: string;
        fromParentId: string | null;
        toParentId: string;
      }>;
      revokedFolderIds?: string[];
    }) => void
  ): () => void;
  // Listen for workspace meta change events
  protected on(
    event: 'workspaceMetaChanged',
    callback: (info: {
      workspaceId: string;
      changeType: string;
      docId?: string | null;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }) => void
  ): () => void;
  // sl-lkdb.3.1: Listen for recovery completed events
  protected on(
    event: 'recoveryCompleted',
    callback: (info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }) => void
  ): () => void;
  // sl-lv2w.5: Listen for doc size warning events
  protected on(
    event: 'docSizeWarning',
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }) => void
  ): () => void;
  // sl-lv2w.5: Listen for doc size hard limit events
  protected on(
    event: 'docSizeHardLimit',
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }) => void
  ): () => void;
  // sl-00ro: Listen for embedding doc status events
  protected on(
    event: 'embeddingDocStatus',
    callback: (info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }) => void
  ): () => void;
  // sl-00ro: Listen for embedding workspace progress events
  protected on(
    event: 'embeddingWorkspaceProgress',
    callback: (info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }) => void
  ): () => void;
  protected on(event: string, callback: (...args: any[]) => void): () => void {
    this.event.on(event, callback);
    return () => {
      this.event.off(event, callback);
    };
  }

  protected emit(event: 'update', update: DocRecord, origin?: string): void;
  protected emit(
    event: 'snapshot',
    snapshot: DocRecord,
    prevSnapshot: DocRecord | null
  ): void;
  protected emit(
    event: 'corruption',
    info: {
      type: 'snapshot' | 'update';
      docId: string;
      timestamp?: Date;
      expected: string;
      actual: string;
    }
  ): void;
  // sl-affine-uhr: Emit when a doc has been compacted on the server
  protected emit(
    event: 'compacted',
    info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }
  ): void;
  // sl-j143.5: Emit when user's permissions changed
  protected emit(
    event: 'permissionsChanged',
    info: {
      eventId?: string;
      workspaceId: string;
      changeType: string;
      timestamp: number;
      permissionGraphVersion?: number;
      refreshMode?: 'scoped' | 'lazy' | 'full';
      affectedFolderIds?: string[];
      affectedDocIds?: string[];
      docPlacementChanges?: Array<{
        docId: string;
        fromFolderId: string | null;
        toFolderId: string;
      }>;
      folderPlacementChanges?: Array<{
        folderId: string;
        fromParentId: string | null;
        toParentId: string;
      }>;
      revokedFolderIds?: string[];
    }
  ): void;
  // Emit when workspace metadata changed
  protected emit(
    event: 'workspaceMetaChanged',
    info: {
      workspaceId: string;
      changeType: string;
      docId?: string | null;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }
  ): void;
  // sl-lkdb.3.1: Emit when workspace recovery completed
  protected emit(
    event: 'recoveryCompleted',
    info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }
  ): void;
  // sl-lv2w.5: Emit doc size warning
  protected emit(
    event: 'docSizeWarning',
    info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }
  ): void;
  // sl-lv2w.5: Emit doc size hard limit
  protected emit(
    event: 'docSizeHardLimit',
    info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }
  ): void;
  // sl-00ro: Emit embedding doc status
  protected emit(
    event: 'embeddingDocStatus',
    info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }
  ): void;
  // sl-00ro: Emit embedding workspace progress
  protected emit(
    event: 'embeddingWorkspaceProgress',
    info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }
  ): void;
  // Emit knowledge graph updated
  protected emit(
    event: 'kgUpdated',
    info: {
      workspaceId: string;
      entityCount: number;
      relationCount: number;
      timestamp: number;
    }
  ): void;
  protected emit(event: string, ...args: any[]): void {
    this.event.emit(event, ...args);
  }

  protected off(event: string, callback: (...args: any[]) => void): void {
    this.event.off(event, callback);
  }

  /**
   * Get a doc snapshot from storage
   */
  protected abstract getDocSnapshot(docId: string): Promise<DocRecord | null>;
  /**
   * Set the doc snapshot into storage
   *
   * @safety
   * be careful when implementing this method.
   *
   * It might be called with outdated snapshot when running in multi-thread environment.
   *
   * A common solution is update the snapshot record is DB only when the coming one's timestamp is newer.
   *
   * @example
   * ```ts
   * await using _lock = await this.lockDocForUpdate(docId);
   * // set snapshot
   *
   * ```
   */
  protected abstract setDocSnapshot(
    snapshot: DocRecord,
    prevSnapshot: DocRecord | null
  ): Promise<boolean>;

  /**
   * Get all updates of a doc that haven't been merged into snapshot.
   *
   * Updates queue design exists for a performace concern:
   * A huge amount of write time will be saved if we don't merge updates into snapshot immediately.
   * Updates will be merged into snapshot when the latest doc is requested.
   */
  protected abstract getDocUpdates(docId: string): Promise<DocRecord[]>;

  /**
   * Mark updates as merged into snapshot.
   */
  protected abstract markUpdatesMerged(
    docId: string,
    updates: DocRecord[]
  ): Promise<number>;

  /**
   * Merge doc updates into a single update.
   */
  protected async squash(updates: DocRecord[]): Promise<DocRecord> {
    const lastUpdate = updates.at(-1);
    if (!lastUpdate) {
      throw new Error('No updates to be squashed.');
    }

    // fast return
    if (updates.length === 1) {
      return lastUpdate;
    }

    const finalUpdate = await this.mergeUpdates(updates.map(u => u.bin));

    return {
      docId: lastUpdate.docId,
      bin: finalUpdate,
      timestamp: lastUpdate.timestamp,
      editor: lastUpdate.editor,
    };
  }

  protected mergeUpdates(updates: Uint8Array[]) {
    const merge = this.options?.mergeUpdates ?? mergeUpdates;

    return merge(updates.filter(bin => !isEmptyUpdate(bin)));
  }

  protected async lockDocForUpdate(docId: string): Promise<AsyncDisposable> {
    return this.locker.lock(`workspace:${this.spaceId}:update`, docId);
  }

  /**
   * Delete only the snapshot for a doc, preserving individual updates.
   * Override in subclasses that support snapshots.
   */
  protected async deleteDocSnapshot(_docId: string): Promise<void> {
    // Default no-op — subclasses with snapshot stores override this
  }

  /**
   * Surgically repair a corrupt doc by validating each IDB entry individually
   * and removing only the corrupt ones. Preserves all valid unsynced data.
   *
   * Algorithm:
   * 1. Read raw snapshot + individual updates (no merge)
   * 2. Validate each with encodeStateVectorFromUpdate (Y.js parse check)
   * 3. Delete only corrupt entries
   * 4. If snapshot was corrupt, rebuild from valid updates or delete
   * 5. Verify the doc is now readable
   */
  async repairDoc(docId: string): Promise<DocRepairResult> {
    await using _lock = await this.lockDocForUpdate(docId);

    const updates = await this.getDocUpdates(docId);

    // Y.js best practice for corrupt local data: nuke and let server re-sync.
    // Individual entry validation is unreliable — entries can be individually
    // valid but produce "Integer out of Range" when merged.
    // See: https://discuss.yjs.dev/t/caught-error-while-handling-a-yjs-update-rangeerror/1789
    await this.deleteDocSnapshot(docId);
    if (updates.length > 0) {
      await this.markUpdatesMerged(docId, updates);
    }

    console.warn(
      `[DocStorage:repair] Cleared all local data for ${docId} ` +
        `(snapshot + ${updates.length} updates). Server will re-sync.`
    );

    return {
      kept: 0,
      removed: updates.length,
      snapshotRemoved: true,
      docReadable: false,
    };
  }
}
